// SchedulerToast — Listener de `apolo:toast` event que mostra notificações
// transientes (info/success/error) no canto inferior direito.
//
// Usado pelo useLocalScheduler pra avisar quando uma task local roda.
// Auto-dismiss em 4s (success/info) ou 8s (error). Empilha até 4
// toasts simultâneos; mais antigos somem.

import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';

const MAX_TOASTS = 4;
const DEFAULT_DURATION = { info: 4000, success: 4000, error: 8000 };

export default function SchedulerToast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    function onToast(e) {
      const detail = e.detail || {};
      const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const kind = detail.kind || 'info';
      const message = detail.message || '';
      const duration = detail.duration || DEFAULT_DURATION[kind] || 4000;
      if (!message) return;

      setToasts((prev) => {
        const next = [...prev, { id, kind, message }];
        return next.slice(-MAX_TOASTS);
      });

      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
    window.addEventListener('apolo:toast', onToast);
    return () => window.removeEventListener('apolo:toast', onToast);
  }, []);

  function dismiss(id) {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      <AnimatePresence initial={false}>
        {toasts.map((t) => {
          const Icon = t.kind === 'success' ? CheckCircle2 : t.kind === 'error' ? AlertCircle : Info;
          const iconColor = t.kind === 'success'
            ? 'text-emerald-400'
            : t.kind === 'error'
            ? 'text-red-400'
            : 'text-apolo-accent';
          const borderColor = t.kind === 'success'
            ? 'border-emerald-400/30'
            : t.kind === 'error'
            ? 'border-red-400/30'
            : 'border-apolo-borderSubtle';
          return (
            <motion.div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              initial={{ opacity: 0, x: 24, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 24, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
              className={`pointer-events-auto flex max-w-sm items-start gap-2 rounded-lg border ${borderColor} bg-apolo-surface1/95 px-3 py-2 shadow-lg backdrop-blur-sm`}
            >
              <Icon size={13} aria-hidden="true" className={`mt-0.5 shrink-0 ${iconColor}`} />
              <p className="flex-1 text-[12px] leading-relaxed text-apolo-textPrimary">{t.message}</p>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Fechar notificação"
                className="shrink-0 rounded text-apolo-textTertiary transition hover:text-apolo-textPrimary"
              >
                <X size={11} aria-hidden="true" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
